import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import {
  EventCategory,
  ErrorObject,
  TelemetryEvent,
  GenericTelemetryEvent,
  ActivityData,
  UserActionData,
  ActivityResultType
} from './TelemetryEvents';

/**
 * Checks for a defined logger and sends the appropriate telemetry event based on the EventCategory
 * @param eventCategory - the type of event category logging required
 * @param logger - Potential Logger passed to do the logging
 * @param event - Event to send based on the event category selected
 * @param error - Optional ErrorObject for event categories that are capable of logging errors, e.g Error and Activity events
 */
export function tryLogEvent(
  eventCategory: EventCategory,
  logger: ITelemetryBaseLogger | undefined,
  event: GenericTelemetryEvent | TelemetryEvent | ActivityData | UserActionData,
  error?: ErrorObject
): void {
  if (!logger) {
    return;
  }
  switch (eventCategory) {
    case EventCategory.Generic:
      return sendTelemetryEvent(logger, event);
    case EventCategory.Error:
      return sendErrorEvent(logger, event, error);
    case EventCategory.Activity:
      return sendActivityEvent(logger, event, error);
    case EventCategory.UserAction:
      return sendUserActionEvent(logger, event);
  }
}

/**
 * Send generic telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 */
export function sendTelemetryEvent(logger: ITelemetryBaseLogger, event: GenericTelemetryEvent): void {
  logger.send({ ...event, category: EventCategory.Generic });
}

/**
 * Send error telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 * @param error - Optional Error object passed with the error event
 */
export function sendErrorEvent(logger: ITelemetryBaseLogger, event: TelemetryEvent, error?: ErrorObject): void {
  if (error) {
    prepareErrorObject(event, error);
  }

  logger.send({ ...event, category: EventCategory.Error });
}

/**
 * Send activity telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 * @param error - Optional Error object passed with the activity event in case of activity failure
 */
export function sendActivityEvent(logger: ITelemetryBaseLogger, event: ActivityData, error?: ErrorObject): void {
  if (error) {
    prepareErrorObject(event, error);
  }

  // If the callers do not provide resultType, use succeeded if present to derive the resultType
  if (!event.resultType && event.succeeded) {
    event.resultType = event.succeeded ? ActivityResultType.Success : ActivityResultType.Failure;
  }

  logger.send({ ...event, category: EventCategory.Activity });
}

/**
 * Send User Action telemetry event
 * Since the helper has a predefined category, please do not provide a category field in the event
 * @param logger - Logger passed to do the logging
 * @param event - Event to send
 */
export function sendUserActionEvent(logger: ITelemetryBaseLogger, event: UserActionData): void {
  logger.send({ ...event, category: EventCategory.UserAction });
}

function prepareErrorObject(event: TelemetryEvent, error: ErrorObject) {
  if (error === null || typeof error !== 'object') {
    event.message = error;
  } else {
    // WARNING: Exceptions can contain PII!
    // For example, XHR will throw object derived from Error that contains config information
    // for failed request, including all the headers, and thus - user tokens!
    // Extract only call stack and message
    event.stack = error.stack;
    const errorMessage = error.message || JSON.stringify(error);
    if (errorMessage) {
      if (event.message) {
        event.message = `${event.message}\n${errorMessage}`;
      } else {
        event.message = errorMessage;
      }
    }
  }

  // Collect stack if we were not able to extract it from error
  if (event.stack === undefined) {
    event.stack = getStack();
  }
}

function getStack(): string | undefined {
  // Some browsers will populate stack right away, others require throwing Error
  let stack = new Error().stack;
  if (!stack) {
    try {
      throw new Error();
    } catch (e) {
      stack = e.stack;
    }
  }
  return stack;
}
